import React, { useEffect, useMemo, useState } from 'react';
import {
  Link,
  Prompt,
  RouteComponentProps,
  useHistory,
} from 'react-router-dom';
import { Location } from 'history';
import { useSelector } from 'react-redux';
import { Alert, Button, Modal } from 'react-bootstrap/lib';
import { UserRole } from '@upstart/harmony/lib/constants';
import { MessageType } from '@upstart/harmony/lib/legos/websiteTypes';
import {
  isLendingStateCode,
  LendingStateCode,
  RicStateSchemaContent,
  RicStateSchemaRevision,
} from 'hooks/services';
import { useRicStateSchema } from 'hooks/api/ricStateSchemas';
import { PageMessage } from 'components/GlobalUI';
import { getCurrentUser } from '../../redux/user/selectors';
import SidebarContainer from '../SidebarContainer';
import RicSchemaPreview from './RicSchemaPreview';
import RicSchemaRevisionHistory, {
  describeRevision,
} from './RicSchemaRevisionHistory';
import './RicSchemaEditor.scss';

const DISCARD_MESSAGE =
  'There are unsaved changes to this schema. Leaving this page discards them.';

/** Two spaces, so a state copied out of one environment diffs against another. */
const format = (content: RicStateSchemaContent) =>
  JSON.stringify(content, null, 2);

function RicSchemaEditorForState({
  stateCode,
}: {
  stateCode: LendingStateCode;
}) {
  const history = useHistory();
  // `PrivateRoute` concatenates all three admin roles onto whatever `roles` it is
  // given, so the endpoint's super-admin-only tier cannot be a route prop.
  const currentUser = useSelector(getCurrentUser);
  const canSave = !!currentUser?.roles?.includes(UserRole.SUPER_ADMIN);

  const {
    stateSchema,
    isLoadingStateSchema,
    stateSchemaError,
    saveStateSchema,
    isSavingStateSchema,
  } = useRicStateSchema(stateCode);

  const [text, setText] = useState<string>();
  const [contentAwaitingConfirm, setContentAwaitingConfirm] =
    useState<RicStateSchemaContent>();
  const [saveError, setSaveError] = useState<string>();
  const [showPreview, setShowPreview] = useState(false);
  const [requestedPath, setRequestedPath] = useState<string>();
  const [viewedRevision, setViewedRevision] =
    useState<RicStateSchemaRevision>();
  const [revisionAwaitingDiscard, setRevisionAwaitingDiscard] =
    useState<RicStateSchemaRevision>();

  const savedText = stateSchema && format(stateSchema.content);

  // Refires on the save's revalidation, making just-saved content the baseline.
  useEffect(() => {
    if (savedText) {
      setText(savedText);
    }
  }, [savedText]);

  // Shape only. The content schema and cross-field rules stay with the endpoint,
  // so Chairman holds no second copy of them.
  const parsed = useMemo(() => {
    if (text === undefined) {
      return {};
    }
    try {
      return { content: JSON.parse(text) as RicStateSchemaContent };
    } catch (error) {
      return {
        parseError: error instanceof Error ? error.message : 'Invalid JSON',
      };
    }
  }, [text]);

  const hasUnsavedChanges = text !== undefined && text !== savedText;

  useEffect(() => {
    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
        event.returnValue = DISCARD_MESSAGE;
      }
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [hasUnsavedChanges]);

  // Returning false blocks the navigation. `Prompt` below then stops guarding while
  // the dialog is open, or confirming it would be blocked in turn.
  const onBeforeNavigation = (location: Location) => {
    if (location.pathname === history.location.pathname) {
      return true;
    }
    setRequestedPath(`${location.pathname}${location.search}`);
    return false;
  };

  const discardAndNavigate = () => {
    if (requestedPath) {
      history.push(requestedPath);
    }
  };

  const loadRevision = (revision: RicStateSchemaRevision) => {
    setText(format(revision.content));
    setViewedRevision(revision);
    setSaveError(undefined);
    setRevisionAwaitingDiscard(undefined);
  };

  // `Prompt` only sees route changes, and this replaces the edit in place.
  const onLoadRevision = (revision: RicStateSchemaRevision) => {
    if (hasUnsavedChanges) {
      setRevisionAwaitingDiscard(revision);
      return;
    }
    loadRevision(revision);
  };

  const confirmSave = async () => {
    if (!contentAwaitingConfirm) {
      return;
    }
    try {
      await saveStateSchema({ content: contentAwaitingConfirm });
      setSaveError(undefined);
      // The loaded revision is the current schema now, so the banner has nothing
      // left to warn about.
      setViewedRevision(undefined);
      // A toast rather than a banner: there is nothing left to act on, unlike a
      // rejection, which stays on screen until the content is fixed.
      PageMessage({
        headline: 'Saved',
        message: `${stateCode}'s RIC schema is now what every consumer reads.`,
        type: MessageType.SUCCESS,
      });
    } catch (error) {
      setSaveError(
        error instanceof Error
          ? error.message
          : `Could not save the schema for ${stateCode}. Please try again.`,
      );
    }
    setContentAwaitingConfirm(undefined);
  };

  if (stateSchemaError) {
    // A missing row 404s with a message that already names the seed script.
    return (
      <Alert bsStyle="danger">
        Could not load the RIC schema for {stateCode}:{' '}
        {stateSchemaError.message}
      </Alert>
    );
  }

  // `text` trails the fetch by the render the effect above runs on.
  if (isLoadingStateSchema || text === undefined) {
    return <Alert>Loading...</Alert>;
  }

  return (
    <>
      <Prompt
        when={hasUnsavedChanges && !requestedPath}
        message={onBeforeNavigation}
      />

      <div className="ric-schema-editor-actions">
        <Link className="btn btn-default" to="/admin/ric-schema">
          Back to all states
        </Link>
        <Button onClick={() => setShowPreview(!showPreview)}>
          {showPreview ? 'Hide preview' : 'Show preview'}
        </Button>
        <Button
          disabled={!parsed.content}
          onClick={() => parsed.content && setText(format(parsed.content))}
        >
          Reformat
        </Button>
        <Button
          bsStyle="primary"
          disabled={
            !canSave ||
            !hasUnsavedChanges ||
            !parsed.content ||
            isSavingStateSchema
          }
          onClick={() => {
            setSaveError(undefined);
            setContentAwaitingConfirm(parsed.content);
          }}
        >
          {isSavingStateSchema ? 'Saving...' : 'Save'}
        </Button>
        {hasUnsavedChanges && (
          <span className="ric-schema-editor-dirty">Unsaved changes</span>
        )}
      </div>

      {!canSave && (
        <Alert bsStyle="info">
          Editing a RIC schema is restricted to super admins. You can browse and
          preview this schema, but not save it.
        </Alert>
      )}

      {/* Stays put through further edits: what is in the editor is no longer the
          live schema, which is worth knowing right up to the save. */}
      {viewedRevision && (
        <Alert bsStyle="warning">
          Viewing the save from {describeRevision(viewedRevision)}. Saving this
          appends a new revision rather than restoring the old one in place, and
          you can edit it further first.
        </Alert>
      )}

      {parsed.parseError && (
        <Alert bsStyle="danger">Not valid JSON: {parsed.parseError}</Alert>
      )}

      {/* Form-level: a cross-field rejection belongs to no single field. */}
      {saveError && (
        <Alert bsStyle="danger">The schema was not saved: {saveError}</Alert>
      )}

      {/* One at a time: the RIC form is laid out for a whole page, not half of one. */}
      {!showPreview && (
        <textarea
          aria-label={`RIC schema JSON for ${stateCode}`}
          className="form-control ric-schema-editor-json"
          spellCheck={false}
          value={text}
          onChange={(event) => setText(event.target.value)}
        />
      )}
      {showPreview &&
        (parsed.content ? (
          <RicSchemaPreview content={parsed.content} />
        ) : (
          <Alert bsStyle="warning">
            Preview unavailable while the JSON is invalid
          </Alert>
        ))}

      {stateSchema && (
        <RicSchemaRevisionHistory
          stateCode={stateCode}
          currentContent={stateSchema.content}
          onLoadRevision={onLoadRevision}
        />
      )}

      <Modal
        show={!!contentAwaitingConfirm}
        onHide={() =>
          !isSavingStateSchema && setContentAwaitingConfirm(undefined)
        }
        backdrop="static"
      >
        <Modal.Header>
          <Modal.Title>Save the RIC schema for {stateCode}?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          This replaces {stateCode}&apos;s current schema. There is no draft
          step and no approval: once the RIC read path is on the database, every
          consumer picks this up on its next request. The schema it replaces
          stays in the revision history.
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => setContentAwaitingConfirm(undefined)}
            disabled={isSavingStateSchema}
          >
            Cancel
          </Button>
          <Button
            bsStyle="danger"
            onClick={confirmSave}
            disabled={isSavingStateSchema}
          >
            {isSavingStateSchema ? 'Saving...' : `Save ${stateCode}`}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!requestedPath} onHide={() => setRequestedPath(undefined)}>
        <Modal.Header>
          <Modal.Title>Discard unsaved changes?</Modal.Title>
        </Modal.Header>
        <Modal.Body>{DISCARD_MESSAGE}</Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setRequestedPath(undefined)}>
            Keep editing
          </Button>
          <Button bsStyle="danger" onClick={discardAndNavigate}>
            Discard and leave
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={!!revisionAwaitingDiscard}
        onHide={() => setRevisionAwaitingDiscard(undefined)}
      >
        <Modal.Header>
          <Modal.Title>Discard unsaved changes?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Loading this revision replaces what is in the editor. Your unsaved
          changes to {stateCode} are lost.
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setRevisionAwaitingDiscard(undefined)}>
            Keep editing
          </Button>
          <Button
            bsStyle="danger"
            onClick={() =>
              revisionAwaitingDiscard && loadRevision(revisionAwaitingDiscard)
            }
          >
            Discard and load
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

type RicSchemaEditorProps = RouteComponentProps<{ stateCode: string }>;

function RicSchemaEditor({ match }: RicSchemaEditorProps) {
  const { stateCode } = match.params;

  return (
    <SidebarContainer title={`RIC Schema — ${stateCode}`}>
      <div className="dealers-table">
        <div className="dealer-list-container">
          {isLendingStateCode(stateCode) ? (
            // Keyed so a URL switch cannot leave one state's edits under another.
            <RicSchemaEditorForState key={stateCode} stateCode={stateCode} />
          ) : (
            <Alert bsStyle="danger">
              {stateCode} is not an Upstart lending state.{' '}
              <Link to="/admin/ric-schema">Back to all states</Link>
            </Alert>
          )}
        </div>
      </div>
    </SidebarContainer>
  );
}

export default RicSchemaEditor;
